import type { PropostaStatus } from "@/types/finance";

export interface PropostaStatusInfo {
  id: PropostaStatus;
  label: string;
  badgeClass: string;
}

/** Fonte única dos rótulos e cores de status de proposta, no molde de `vaga-status.ts`. */
export const PROPOSTA_STATUS_LIST: readonly PropostaStatusInfo[] = [
  { id: "rascunho", label: "Rascunho", badgeClass: "bg-slate-50 text-slate-700 border-slate-200" },
  { id: "gerada", label: "Gerada", badgeClass: "bg-blue-50 text-blue-700 border-blue-200" },
  { id: "enviada", label: "Enviada", badgeClass: "bg-purple-50 text-purple-700 border-purple-200" },
  { id: "aceita", label: "Aceita", badgeClass: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  { id: "lancada_omie", label: "Lançada no Omie", badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { id: "recusada", label: "Recusada", badgeClass: "bg-red-50 text-red-700 border-red-200" },
  { id: "cancelada", label: "Cancelada", badgeClass: "bg-slate-100 text-slate-500 border-slate-200" },
] as const;

const STATUS_BY_ID = new Map(PROPOSTA_STATUS_LIST.map((status) => [status.id, status]));

export function getPropostaStatusInfo(status: PropostaStatus): PropostaStatusInfo {
  return STATUS_BY_ID.get(status) ?? PROPOSTA_STATUS_LIST[0];
}

/** Aceita pela agência, com ou sem as OS já lançadas no Omie. */
export function propostaFoiAceita(status: PropostaStatus | null): boolean {
  return status === "aceita" || status === "lancada_omie";
}

/** Dados, itens e documento ficam editáveis de rascunho até aceita, enquanto nenhuma OS foi para o Omie. Espelha `proposal_accepts_edits` da API. */
export function propostaEhEditavel(status: PropostaStatus, osEnviadaAoOmie: boolean): boolean {
  const aberta = status === "rascunho" || status === "gerada" || status === "enviada" || status === "aceita";
  return aberta && !osEnviadaAoOmie;
}

export function propostaPodeSerCancelada(status: PropostaStatus): boolean {
  return status === "rascunho" || status === "gerada";
}

/** Por que a proposta não aceita mais edição, para dizer na tela em vez de só apagar o botão. */
export function motivoDoBloqueioDaEdicao(status: PropostaStatus, osEnviadaAoOmie: boolean): string | null {
  if (osEnviadaAoOmie || status === "lancada_omie") {
    return "A proposta já tem OS no Omie, que a plataforma não altera: ajustes são feitos direto no Omie.";
  }
  if (status === "recusada") return "A proposta foi recusada. Reabra para gerar uma nova versão.";
  if (status === "cancelada") return "A proposta foi cancelada e não gera mais versões.";
  return null;
}
